import React, { useEffect, useState } from "react";
import { collection, onSnapshot, query, orderBy, doc, setDoc } from "firebase/firestore";
import AdminLayout from "../../components/layouts/AdminLayout";
import { Building2, Loader2, ShieldCheck, Users2 } from "lucide-react";
import { firestore as firestoreInstance } from "../../firebaseConfig";
import SubscriptionInfoCard from "../company/CompanySettings/SubscriptionInfoCard";
import PaymentsTab from "../company/components/PaymentsTab";
import FarmerTable from "../company/components/FarmerTable";
import ClientTable from "../company/components/ClientTable";
import AdminCompanyEmployees from "./AdminCompanyEmployees";
import AccountTransactionModal from "../../components/AccountTransactionModal";
import { isFutureDate } from "../../utils/companyAccess";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const tabs = ["Overview", "Farmers", "Clients", "Employees", "Payments"];
const firestore = firestoreInstance || null;
const LIST_MAX_HEIGHT_CLASS = "max-h-[520px]";
const RECEIPT_DATE_LOCALE = "en-US";
const RECEIPT_BILLING_MONTH_OPTIONS = { month: "long", year: "numeric" };
const RECEIPT_PAYMENT_DATE_OPTIONS = {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};
const RECEIPT_LAYOUT = {
  marginLeft: 14,
  headerTitleY: 18,
  detailsStartY: 28,
  detailSpacing: 8,
  tableStartY: 68,
  summaryStartOffset: 10,
  summaryLineSpacing: 8,
  notesStartOffset: 36,
  notesTextOffset: 42,
  notesMaxWidth: 180,
};
const RECEIPT_TABLE_HEADERS = [
  "Service",
  "Details",
  "Unit",
  "Qty",
  "Unit Price (USD)",
  "Line Total (USD)",
];
const DEFAULT_EMAIL_COMPANY = "company";
const CURRENCY_ROUNDING_FACTOR = 100;
const RECEIPT_CONTACT_LINE =
  "Neuracraft | Nairobi, Kenya | +254768949692 | support@neuracraft.com";
const RECEIPT_THANK_YOU_LINE = "Thank you for trusting Neuracraft.";
const normalizeSubscriptionDate = (expiryDate) => {
  if (typeof expiryDate !== "string") return "";
  if (expiryDate.includes("T")) return expiryDate.split("T")[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(expiryDate) ? expiryDate : "";
};

const AdminCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("Overview");
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [modalCompanyId, setModalCompanyId] = useState(null);
  const [invoiceCompanyId, setInvoiceCompanyId] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [receiptCompanyId, setReceiptCompanyId] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [manageCompanyId, setManageCompanyId] = useState(null);
  const [subscriptionRefreshSeed, setSubscriptionRefreshSeed] = useState(0);

  const getCompanyInitial = (company) =>
    company?.companyName?.[0] || company?.id?.[0] || "C";

  useEffect(() => {
    if (!firestore) {
      setLoading(false);
      setError("Firestore not initialized");
      return;
    }
    const q = query(
      collection(firestore, "companies"),
      orderBy("companyName", "asc")
    );
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCompanies(data);
        setSelectedCompanyId((prev) => {
          if (prev && data.some((d) => d.id === prev)) return prev;
          return data[0]?.id || null;
        });
        setLoading(false);
      },
      (err) => {
        console.error("companies snapshot error", err);
        setError("Failed to load companies");
        setLoading(false);
      }
    );
    return () => unsub();
  }, [firestore]);

  const selectedCompany =
    companies.find((c) => c.id === selectedCompanyId) || null;
  const forcedCompanyName =
    selectedCompany?.companyName || selectedCompany?.id || "";

  return (
    <AdminLayout>
      <div className="space-y-6">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center border border-blue-100">
              <Building2 className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-slate-900">
                Companies
              </h1>
              <p className="text-sm text-slate-600">
                Major admin hub: pick a company, inspect profile, and manage
                members.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${
                  activeTab === tab
                    ? "bg-blue-50 text-blue-700 border-blue-200"
                    : "text-slate-600 border-slate-200 hover:bg-slate-50"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </header>

        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
              {selectedCompany ? (
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-cyan-100 to-indigo-50 flex items-center justify-center border overflow-hidden">
                    {selectedCompany.logoUrl ? (
                      <img
                        src={selectedCompany.logoUrl}
                        alt="logo"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-indigo-700 font-semibold text-lg">
                        {getCompanyInitial(selectedCompany)}
                      </span>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">
                          {selectedCompany.companyName || selectedCompany.id}
                        </h3>
                        <p className="text-sm text-slate-600">
                          {selectedCompany.industry || "Industry not set"}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                            selectedCompany.status === "Suspended"
                              ? "bg-rose-50 text-rose-700 border-rose-100"
                              : selectedCompany.status === "Pending"
                              ? "bg-amber-50 text-amber-700 border-amber-100"
                              : "bg-emerald-50 text-emerald-700 border-emerald-100"
                          }`}
                        >
                          {selectedCompany.status || "Active"}
                        </span>
                        <button
                          onClick={() => {
                            setManageCompanyId(selectedCompany.id);
                            setShowManageModal(true);
                          }}
                          className="px-3 py-1 rounded-lg text-xs font-semibold border border-slate-200 text-slate-700 hover:bg-slate-50"
                        >
                          Manage
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 text-sm">
                      <div>
                        <div className="text-xs text-slate-500">Email</div>
                        <div className="text-slate-800">
                          {selectedCompany.email || "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">
                          Registration
                        </div>
                        <div className="text-slate-800">
                          {selectedCompany.registrationNumber || "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Phone</div>
                        <div className="text-slate-800">
                          {selectedCompany.phone || "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">
                          Default currency
                        </div>
                        <div className="text-slate-800">
                          {selectedCompany.defaultCurrency || "—"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-600">
                  Select a company to view details.
                </div>
              )}
            </div>

            <SubscriptionInfoCard
              key={`${selectedCompanyId || "none"}-${subscriptionRefreshSeed}`}
              companyKey={selectedCompanyId}
            />

            {activeTab === "Payments" ? (
              <div className="bg-white border border-slate-100 rounded-xl p-1 shadow-sm max-h-[80vh] overflow-y-auto">
                {selectedCompany ? (
                  <PaymentsTab
                    key={selectedCompany.id}
                    forcedCompanyName={forcedCompanyName}
                    compactView
                  />
                ) : (
                  <div className="p-4 text-sm text-slate-600">
                    Select a company to view payments.
                  </div>
                )}
              </div>
            ) : activeTab === "Employees" ? (
              <AdminCompanyEmployees companyId={selectedCompanyId} />
            ) : activeTab === "Farmers" ? (
              <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
                {forcedCompanyName ? (
                  <FarmerTable companyName={forcedCompanyName} compact />
                ) : (
                  <div className="text-sm text-slate-600">
                    Select a company to view farmers.
                  </div>
                )}
              </div>
            ) : activeTab === "Clients" ? (
              <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
                {forcedCompanyName ? (
                  <ClientTable companyName={forcedCompanyName} compact />
                ) : (
                  <div className="text-sm text-slate-600">
                    Select a company to view clients.
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <ShieldCheck className="h-5 w-5 text-emerald-600" />
                  <p className="text-sm font-semibold text-slate-900">
                    {activeTab} (placeholder)
                  </p>
                </div>
                <p className="text-sm text-slate-600">
                  Overview of farmers, clients, and employees will appear here.
                </p>
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Users2 className="h-5 w-5 text-blue-600" />
                <p className="text-sm font-semibold text-slate-900">
                  Companies
                </p>
              </div>
              {loading && (
                <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
              )}
            </div>

            <div
              className={`space-y-2 overflow-auto pr-1 ${LIST_MAX_HEIGHT_CLASS}`}
            >
              {loading && (
                <div className="space-y-2">
                  <div className="h-12 rounded-lg bg-slate-100 animate-pulse" />
                  <div className="h-12 rounded-lg bg-slate-100 animate-pulse" />
                </div>
              )}

              {!loading && error && (
                <div className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg p-3">
                  {error}
                </div>
              )}

              {!loading && !error && companies.length === 0 && (
                <div className="text-sm text-slate-600">
                  No companies found.
                </div>
              )}

              {!loading &&
                !error &&
                companies.map((company) => (
                  <div
                    key={company.id}
                    onClick={() => setSelectedCompanyId(company.id)}
                    className={`w-full px-3 py-2 rounded-lg border transition flex items-start gap-3 ${
                      selectedCompanyId === company.id
                        ? "border-blue-200 bg-blue-50"
                        : "border-slate-200 hover:bg-slate-50"
                    }`}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-slate-900">
                        {company.companyName || company.id}
                      </div>
                      <div className="text-xs text-slate-500">
                        {company.industry || "Industry not set"} ·{" "}
                        {company.email || "No email"}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setModalCompanyId(company.id);
                        setShowAccountModal(true);
                      }}
                      className="text-xs px-2 py-1 rounded border border-cyan-200 bg-cyan-50 text-cyan-700 hover:bg-cyan-100"
                    >
                      Adjust bank
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setInvoiceCompanyId(company.id);
                        setShowInvoiceModal(true);
                      }}
                      className="text-xs px-2 py-1 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                    >
                      Invoice
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setReceiptCompanyId(company.id);
                        setShowReceiptModal(true);
                      }}
                      className="text-xs px-2 py-1 rounded border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                    >
                      Receipt
                    </button>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {showAccountModal && modalCompanyId && (
          <AccountTransactionModal
            companyKey={modalCompanyId}
            accountType="company_bank"
            onClose={() => setShowAccountModal(false)}
            onSaved={() => setShowAccountModal(false)}
          />
        )}

        {showInvoiceModal && invoiceCompanyId && (
          <InvoiceModal
            company={companies.find((c) => c.id === invoiceCompanyId) || null}
            onClose={() => {
              setShowInvoiceModal(false);
              setInvoiceCompanyId(null);
            }}
          />
        )}

        {showReceiptModal && receiptCompanyId && (
          <ReceiptModal
            company={companies.find((c) => c.id === receiptCompanyId) || null}
            onClose={() => {
              setShowReceiptModal(false);
              setReceiptCompanyId(null);
            }}
          />
        )}

        {showManageModal && manageCompanyId && (
          <ManageCompanyModal
            company={companies.find((c) => c.id === manageCompanyId) || null}
            onClose={() => {
              setShowManageModal(false);
              setManageCompanyId(null);
            }}
            onSaved={() => {
              setSubscriptionRefreshSeed((prev) => prev + 1);
              setShowManageModal(false);
              setManageCompanyId(null);
            }}
          />
        )}
      </div>
    </AdminLayout>
  );
};

// Status metadata — label, colour classes, and a short description shown in the UI
const STATUS_OPTIONS = [
  {
    value: "active",
    label: "Active",
    description: "Company has full access to the system.",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
    btn: "bg-emerald-600 hover:bg-emerald-700",
  },
  {
    value: "paused",
    label: "Paused",
    description: "Temporarily restricts access. Can be reactivated at any time.",
    badge: "bg-amber-50 text-amber-700 border-amber-200",
    btn: "bg-amber-500 hover:bg-amber-600",
  },
  {
    value: "suspended",
    label: "Suspended",
    description: "Access blocked pending review or payment.",
    badge: "bg-orange-50 text-orange-700 border-orange-200",
    btn: "bg-orange-500 hover:bg-orange-600",
  },
  {
    value: "blocked",
    label: "Blocked",
    description: "Permanently locked out. Use for policy violations or fraud.",
    badge: "bg-rose-50 text-rose-700 border-rose-200",
    btn: "bg-rose-600 hover:bg-rose-700",
  },
];

const ManageCompanyModal = ({ company, onClose, onSaved }) => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split("T")[0];

  const [status, setStatus] = useState("active");
  const [expiresAt, setExpiresAt] = useState(tomorrowStr);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const selectedStatus = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];

  const handleSave = async () => {
    setError("");
    setSuccess(false);

    if (status === "active" && (!expiresAt || expiresAt <= new Date().toISOString().split("T")[0])) {
      setError("Subscription end date must be in the future when activating.");
      return;
    }
    if ((status === "suspended" || status === "blocked") && !reason.trim()) {
      setError("Please provide a reason when suspending or blocking a company.");
      return;
    }

    try {
      setSaving(true);
      const companyKey = company.id;
      const canOperate =
        status === "active" && expiresAt && isFutureDate(expiresAt);
      const firestorePayload = {
        subscriptionStatus: status,
        subscriptionStatusReason: reason.trim() || "",
        subscriptionExpiresAt: status === "active" && expiresAt ? expiresAt : null,
        status,
        status_reason: reason.trim() || "",
        subscription_expires_at: status === "active" && expiresAt ? expiresAt : null,
        subscription_active: canOperate,
        can_operate: canOperate,
        updatedAt: Date.now(),
      };

      if (!firestoreInstance) {
        throw new Error("Firestore is not available.");
      }
      await setDoc(doc(firestoreInstance, "companies", companyKey), firestorePayload, {
        merge: true,
      });
      await setDoc(
        doc(
          firestoreInstance,
          "companies",
          companyKey,
          "subscriptionSnapshots",
          "current"
        ),
        {
          ...firestorePayload,
          snapshotAt: Date.now(),
        },
        { merge: true }
      );
      setSuccess(true);
      setTimeout(() => onSaved(), 1200);
      return;

    } catch (err) {
      console.error("ManageCompanyModal error:", err);
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-2 sm:px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">

        {/* Header */}
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Manage subscription</h3>
            <p className="text-xs text-slate-500 mt-0.5">{company?.companyName || company?.id}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-lg leading-none">✕</button>
        </div>

        <div className="p-5 space-y-5">

          {/* Status selector */}
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-2">Access status</label>
            <div className="grid grid-cols-2 gap-2">
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setStatus(opt.value)}
                  className={`text-left px-3 py-2.5 rounded-lg border text-sm transition ${
                    status === opt.value
                      ? `${opt.badge} border-current font-semibold`
                      : "border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <div className="font-medium">{opt.label}</div>
                  <div className="text-xs opacity-70 mt-0.5 leading-tight">{opt.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Subscription end date — only when activating */}
          {status === "active" && (
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">
                Subscription end date
              </label>
              <input
                type="date"
                value={expiresAt}
                min={tomorrowStr}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
              />
              <p className="text-xs text-slate-400 mt-1">
                Company access will be unlocked until end of this date.
              </p>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">
              Reason{" "}
              {(status === "suspended" || status === "blocked") ? (
                <span className="text-rose-500">*</span>
              ) : (
                <span className="text-slate-400 font-normal">(optional)</span>
              )}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder={
                status === "blocked" ? "e.g. Fraudulent activity detected" :
                status === "suspended" ? "e.g. Outstanding invoice — payment required" :
                status === "paused" ? "e.g. Company requested temporary pause" :
                "e.g. Subscription renewed for 1 year"
              }
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
            />
          </div>

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg p-3">
              {error}
            </div>
          )}
          {success && (
            <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg p-3">
              ✓ Company status updated successfully. Closing...
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || success}
            className={`px-4 py-2 text-sm rounded-lg text-white transition disabled:opacity-50 ${selectedStatus.btn}`}
          >
            {saving ? "Saving..." :
              status === "active" ? "Renew & activate" :
              status === "paused" ? "Pause access" :
              status === "suspended" ? "Suspend access" :
              "Block access"}
          </button>
        </div>
      </div>
    </div>
  );
};


const InvoiceModal = ({ company, onClose }) => {
  const baseServices = ["Web", "Mobile", "AI", "Computing", "Databases"];
  const [charges, setCharges] = useState(
    baseServices.map((name) => ({ name, amount: 30 }))
  );
  const [customService, setCustomService] = useState({ name: "", amount: 0 });
  const [notes, setNotes] = useState("");

  const updateCharge = (index, value) => {
    setCharges((prev) =>
      prev.map((c, i) =>
        i === index ? { ...c, amount: Number(value) || 0 } : c
      )
    );
  };

  const handleGenerate = () => {
    const services = [
      ...charges,
      ...(customService.name
        ? [
            {
              name: customService.name,
              amount: Number(customService.amount) || 0,
            },
          ]
        : []),
    ].filter((s) => s.name && Number(s.amount) > 0);

    const total = services.reduce(
      (sum, s) => sum + Number(s.amount || 0),
      0
    );
    const now = new Date();
    const due = new Date();
    due.setDate(now.getDate() + 5);

    const doc = new jsPDF();
    const companyName = company?.companyName || company?.id || "Company";

    doc.setFontSize(16);
    doc.text("Invoice", 14, 18);
    doc.setFontSize(11);
    doc.text(`Company: ${companyName}`, 14, 28);
    doc.text(`Generated: ${now.toLocaleDateString()}`, 14, 36);
    doc.text(`Due date: ${due.toLocaleDateString()}`, 14, 44);
    doc.text("Billing period: Monthly", 14, 52);

    autoTable(doc, {
      startY: 60,
      head: [["Service", "Monthly charge (USD)"]],
      body: services.map((s) => [s.name, `${Number(s.amount).toFixed(2)}`]),
      styles: { fontSize: 10 },
      headStyles: { fillColor: [79, 70, 229] },
    });

    const afterTableY = doc.lastAutoTable?.finalY || 60;
    doc.setFontSize(12);
    doc.text(`Total due: $${total.toFixed(2)}`, 14, afterTableY + 10);
    if (notes) {
      doc.setFontSize(10);
      doc.text("Notes:", 14, afterTableY + 20);
      doc.text(notes, 14, afterTableY + 27, { maxWidth: 180 });
    }

    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFontSize(9);
    doc.text(
      "Contact: support@neuracraft.com | +254 (768) 949-692  | www.neuracraft.com",
      14,
      pageHeight - 20
    );
    doc.text("Thank you for choosing Neuracraft.", 14, pageHeight - 12);

    doc.save(`${companyName}_invoice.pdf`);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-2 sm:px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 sm:px-6 sm:py-4 border-b flex items-center justify-between">
          <h3 className="text-base sm:text-lg font-semibold text-slate-900">
            Generate invoice{" "}
            {company?.companyName ? `for ${company.companyName}` : ""}
          </h3>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 text-sm"
          >
            Close
          </button>
        </div>

        {/* Scrollable body */}
        <div className="px-4 py-3 sm:px-6 sm:py-4 overflow-y-auto flex-1 space-y-4">
          <p className="text-sm text-slate-600">
            Default monthly charges are set to $30 for each service. Adjust
            amounts to match the services the customer uses. Set a value to 0
            to exclude it.
          </p>

          <div className="space-y-3">
            {charges.map((service, idx) => (
              <div
                key={service.name}
                className="flex items-center justify-between gap-3"
              >
                <label className="text-sm font-medium text-slate-800 w-32">
                  {service.name}
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={service.amount}
                  onChange={(e) => updateCharge(idx, e.target.value)}
                  className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                />
                <span className="text-sm text-slate-500">USD</span>
              </div>
            ))}

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <input
                type="text"
                placeholder="Other service"
                value={customService.name}
                onChange={(e) =>
                  setCustomService((p) => ({ ...p, name: e.target.value }))
                }
                className="sm:w-1/2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
              <div className="flex items-center gap-3 sm:w-1/2 w-full">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={customService.amount}
                  onChange={(e) =>
                    setCustomService((p) => ({
                      ...p,
                      amount: Number(e.target.value) || 0,
                    }))
                  }
                  className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  placeholder="Amount"
                />
                <span className="text-sm text-slate-500">USD</span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-800">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
              placeholder="Add any specific terms, payment instructions, or reminders."
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 sm:px-6 sm:py-4 border-t flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 sm:px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            className="px-3 sm:px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
          >
            Generate PDF
          </button>
        </div>
      </div>
    </div>
  );
};

const ReceiptModal = ({ company, onClose }) => {
  const baseServices = [
    {
      name: "Web Application Access",
      unit: "subscription",
      description:
        "Portal access for operations staff (farmer & milk seller management, pricing, payouts dashboard).",
    },
    {
      name: "Mobile App Access",
      unit: "subscription",
      description:
        "Mobile app access for registered farmers and milk sellers with collection and payout tracking.",
    },
    {
      name: "AI Fraud & Anomaly Detection",
      unit: "module",
      description:
        "Automated anomaly detection on milk collection and payment streams.",
    },
    {
      name: "Cloud Infrastructure & Databases",
      unit: "bundle",
      description:
        "Hosting, secure databases, backups, and pipelines for ingestion and analytics.",
    },
    {
      name: "Support & Maintenance",
      unit: "plan",
      description:
        "Email and chat support, updates, and monitoring for uptime and performance.",
    },
  ];

  const [charges, setCharges] = useState(
    baseServices.map((service) => ({ ...service, amount: 30 }))
  );
  const [customService, setCustomService] = useState({
    name: "",
    amount: 0,
    unit: "item",
    description: "",
  });
  const [notes, setNotes] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("M-Pesa B2C");
  const [taxRate, setTaxRate] = useState(16);

  const updateChargeAmount = (index, value) => {
    setCharges((prev) =>
      prev.map((c, i) =>
        i === index ? { ...c, amount: Number(value) || 0 } : c
      )
    );
  };

  const sanitizeEmailSlug = (slugSource) => {
    const cleanedSlug = slugSource
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .replace(/-{2,}/g, "-");
    return cleanedSlug || DEFAULT_EMAIL_COMPANY;
  };

  const roundCurrency = (value) =>
    Math.round(value * CURRENCY_ROUNDING_FACTOR) / CURRENCY_ROUNDING_FACTOR;

  const getCompanyEmail = () => {
    if (company?.email) return company.email;
    const slugSource = company?.companyName || DEFAULT_EMAIL_COMPANY;
    const slug = sanitizeEmailSlug(slugSource);
    return `finance@${slug}.com`;
  };

  const handleGenerateReceipt = () => {
    const services = [
      ...charges,
      ...(customService.name && Number(customService.amount) > 0
        ? [customService]
        : []),
    ].filter((s) => s.name && Number(s.amount) > 0);

    if (services.length === 0) {
      onClose();
      return;
    }

    const now = new Date();
    const billingMonth = now.toLocaleString(
      RECEIPT_DATE_LOCALE,
      RECEIPT_BILLING_MONTH_OPTIONS
    );
    const paymentDate = now.toLocaleString(
      RECEIPT_DATE_LOCALE,
      RECEIPT_PAYMENT_DATE_OPTIONS
    );
    const subtotal = services.reduce(
      (sum, s) => sum + Number(s.amount || 0),
      0
    );
    const vatValue = roundCurrency(subtotal * (taxRate / 100));
    const total = roundCurrency(subtotal + vatValue);

    const doc = new jsPDF();
    const companyName = company?.companyName || company?.id || "Company";
    const customerEmail = getCompanyEmail();
    const {
      marginLeft,
      headerTitleY,
      detailsStartY,
      detailSpacing,
      tableStartY,
      summaryStartOffset,
      summaryLineSpacing,
      notesStartOffset,
      notesTextOffset,
      notesMaxWidth,
    } = RECEIPT_LAYOUT;

    doc.setFontSize(15);
    doc.text("Subscription Receipt", marginLeft, headerTitleY);
    doc.setFontSize(11);
    doc.text(`Company: ${companyName}`, marginLeft, detailsStartY);
    doc.text(
      `Billing month: ${billingMonth}`,
      marginLeft,
      detailsStartY + detailSpacing
    );
    doc.text(
      `Payment date: ${paymentDate}`,
      marginLeft,
      detailsStartY + detailSpacing * 2
    );
    doc.text(
      `Payment method: ${paymentMethod}`,
      marginLeft,
      detailsStartY + detailSpacing * 3
    );
    doc.text(
      `Customer email: ${customerEmail}`,
      marginLeft,
      detailsStartY + detailSpacing * 4
    );

    autoTable(doc, {
      startY: tableStartY,
      head: [RECEIPT_TABLE_HEADERS],
      body: services.map((s) => {
        const lineTotal = Number(s.amount || 0);
        return [
          s.name,
          s.description || "",
          s.unit || "item",
          "1",
          `${Number(s.amount).toFixed(2)}`,
          `${lineTotal.toFixed(2)}`,
        ];
      }),
      styles: { fontSize: 9, cellWidth: "wrap" },
      headStyles: { fillColor: [16, 42, 67] },
      columnStyles: { 1: { cellWidth: 60 } },
    });

    const afterTableY = doc.lastAutoTable?.finalY || tableStartY;
    doc.setFontSize(11);
    doc.text(
      `Subtotal: $${subtotal.toFixed(2)}`,
      marginLeft,
      afterTableY + summaryStartOffset
    );
    doc.text(
      `VAT (${taxRate}%): $${vatValue.toFixed(2)}`,
      marginLeft,
      afterTableY + summaryStartOffset + summaryLineSpacing
    );
    doc.text(
      `Total paid: $${total.toFixed(2)}`,
      marginLeft,
      afterTableY + summaryStartOffset + summaryLineSpacing * 2
    );

    if (notes) {
      doc.setFontSize(10);
      doc.text("Notes:", marginLeft, afterTableY + notesStartOffset);
      doc.text(notes, marginLeft, afterTableY + notesTextOffset, {
        maxWidth: notesMaxWidth,
      });
    }

    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFontSize(9);
    doc.text(RECEIPT_CONTACT_LINE, marginLeft, pageHeight - 18);
    doc.text(RECEIPT_THANK_YOU_LINE, marginLeft, pageHeight - 12);

    doc.save(`${companyName}_receipt.pdf`);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-2 sm:px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 sm:px-6 sm:py-4 border-b flex items-center justify-between">
          <h3 className="text-base sm:text-lg font-semibold text-slate-900">
            Generate receipt{" "}
            {company?.companyName ? `for ${company.companyName}` : ""}
          </h3>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 text-sm"
          >
            Close
          </button>
        </div>

        {/* Scrollable body */}
        <div className="px-4 py-3 sm:px-6 sm:py-4 overflow-y-auto flex-1 space-y-4">
          <p className="text-sm text-slate-600">
            Receipt will include the configured services, payment method, and VAT
            in a professional PDF format.
          </p>

          <div className="space-y-3">
            {charges.map((service, idx) => (
              <div
                key={service.name}
                className="flex items-start justify-between gap-3"
              >
                <div className="flex-1">
                  <label className="text-sm font-medium text-slate-800 block">
                    {service.name}
                  </label>
                  <p className="text-xs text-slate-500">
                    {service.description}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={service.amount}
                    onChange={(e) => updateChargeAmount(idx, e.target.value)}
                    className="w-24 sm:w-28 rounded-lg border border-slate-200 px-2 sm:px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  />
                  <span className="text-xs sm:text-sm text-slate-500">
                    USD
                  </span>
                </div>
              </div>
            ))}

            <div className="space-y-2 border-t pt-3 border-slate-100">
              <label className="text-sm font-medium text-slate-800 block">
                Custom service
              </label>
              <input
                type="text"
                placeholder="Service name"
                value={customService.name}
                onChange={(e) =>
                  setCustomService((p) => ({ ...p, name: e.target.value }))
                }
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
              />
              <textarea
                placeholder="Description (optional)"
                value={customService.description}
                onChange={(e) =>
                  setCustomService((p) => ({
                    ...p,
                    description: e.target.value,
                  }))
                }
                rows={2}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
              />
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={customService.amount}
                  onChange={(e) =>
                    setCustomService((p) => ({
                      ...p,
                      amount: Number(e.target.value) || 0,
                    }))
                  }
                  className="w-28 sm:w-32 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  placeholder="Amount"
                />
                <span className="text-xs sm:text-sm text-slate-500">
                  USD
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-800">
              Payment method
            </label>
            <input
              type="text"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-800">
              VAT rate (%)
            </label>
            <input
              type="number"
              min="0"
              step="0.5"
              value={taxRate}
              onChange={(e) => setTaxRate(Number(e.target.value) || 0)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-800">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
              placeholder="Add payment confirmation details or extra info."
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 sm:px-6 sm:py-4 border-t flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 sm:px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={handleGenerateReceipt}
            className="px-3 sm:px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
          >
            Generate receipt
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminCompanies;
